import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ApiError } from "../../../../services/api.errors";
import { StaffMember } from "../../../../services/settings.service";
import { Role, RolesAPI } from "../../../../services/roles.service";
import { RoleMultiSelect } from "./RoleMultiSelect";

interface Props {
  isOpen:   boolean;
  member:   StaffMember | null;
  onClose:  () => void;
  onSaved:  (userId: string, roles: Role[]) => void;
}

/** "Manage roles" - full-set role assignment for one existing staff member.
 *  Opened from AccessControlModal's staff row. */
export const AssignRolesModal: React.FC<Props> = ({ isOpen, member, onClose, onSaved }) => {
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !member) return;
    setLoading(true);
    RolesAPI.listRoles()
      .then(roles => {
        // Assign the clinic's own clones, never a system template directly -
        // matches the backend's fk_user_roles_role_clinic constraint.
        setAllRoles(roles.filter(r => !r.is_system));
        setSelected(member.roles.map(r => r.role_id));
      })
      .catch(() => toast.error("Failed to load roles."))
      .finally(() => setLoading(false));
  }, [isOpen, member]);

  if (!isOpen || !member) return null;

  const name = `${member.first_name} ${member.last_name}`.trim() || member.email;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await RolesAPI.setStaffRoles(member.user_id, selected);
      onSaved(member.user_id, updated);
      toast.success(`Permissions updated for ${name}.`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed to update roles.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Manage Roles</h2>
            <p className="text-xs text-gray-500 mt-1">{name}</p>
          </div>
          <button onClick={onClose} title="Close" className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
          ) : (
            <RoleMultiSelect roles={allRoles} value={selected} onChange={setSelected} disabled={saving} />
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} disabled={saving}
            className="px-5 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : "Save Roles"}
          </button>
        </div>
      </div>
    </div>
  );
};
